import React from 'react';
import HeroFigure from '../../common/HeroFigure';
import { GemIcon } from './GemArt';
import { starsFor } from '../boardModel';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../../../i18n/format';

/** Top bar: back, mission name, moves left. */
export function TopBar({ title, movesLeft, movesTotal, onBack }) {
  const { t } = useTranslation();
  const low = movesLeft != null && movesLeft <= 3;
  return (
    <div className="m3-topbar">
      <button type="button" className="m3-back" onClick={onBack} aria-label={t('common.back', 'Back')}>‹</button>
      <div className="m3-title">
        <span className="m3-title__kicker">💎 {t('match3.kicker', 'Match-3')}</span>
        <span className="m3-title__name">{title || t('match3.default_title', 'Gem Mine')}</span>
      </div>
      <div className={`m3-moves${low ? ' is-low' : ''}`} data-testid="hud-moves" aria-label={t('game.moves_left', 'Moves left')}>
        <span className="m3-moves__n">{movesLeft ?? '–'}</span>
        <span className="m3-moves__of">/{movesTotal ?? '–'} {t('game.moves', 'moves')}</span>
      </div>
    </div>
  );
}

/** Score bar with ★ markers at T / 1.5T / 2T (the bar is full at 2T) + the hero miniature. */
export function ScorePanel({ score = 0, thresholds, hero, cheer }) {
  const { t } = useTranslation();
  const max = thresholds[2] || 1;
  const pct = Math.max(0, Math.min(100, (score / max) * 100));
  const stars = starsFor(score, thresholds);
  return (
    <div className="m3-score">
      <div className={`m3-hero${cheer ? ' is-cheer' : ''}`} key={cheer ? `c${cheer}` : 'idle'} data-testid="m3-hero">
        <HeroFigure
          look={hero?.look}
          character={hero?.character}
          size={58}
          facing={1}
          pose={cheer ? 'jump' : 'idle'}
        />
      </div>
      <div className="m3-score__main">
        <div className="m3-score__row">
          <span className="m3-score__label">{t('match3.score', 'Score')}</span>
          <span className="m3-score__value" data-testid="hud-score">{formatNumber(score)}</span>
        </div>
        <div className="m3-bar" role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={score}>
          <div className="m3-bar__fill" style={{ width: `${pct}%` }} />
          {thresholds.map((th, i) => (
            <span
              key={th || i}
              className={`m3-bar__star${stars > i ? ' is-on' : ''}`}
              style={{ left: `${(th / max) * 100}%` }}
              title={t('match3.star_at', '{{n}}★ at {{score}}', { n: i + 1, score: th })}
              data-testid={`hud-star-${i + 1}`}
            >
              ★
            </span>
          ))}
        </div>
        <div className="m3-score__goal">{t('match3.goal', 'Goal {{n}}', { n: formatNumber(thresholds[0] || 0) })}</div>
      </div>
    </div>
  );
}

/** Hero build bonus per gem colour (from state.gemBonus). */
export function GemBonusBar({ gemBonus }) {
  const { t } = useTranslation();
  if (!gemBonus) return null;
  const items = ['STR', 'AGI', 'INT'].filter((g) => gemBonus[g] != null);
  return (
    <div className="m3-bonus" aria-label={t('match3.gem_bonus_aria', 'Hero gem bonus')}>
      {items.map((g) => (
        <span key={g} className="m3-bonus__chip">
          <GemIcon gem={g} size={18} />
          <span>{g}</span>
          <b>×{Number(gemBonus[g]).toFixed(2)}</b>
        </span>
      ))}
    </div>
  );
}
